import { useRef, useCallback } from 'react';
import { clamp, findNearestScroller } from '@utils/common';
import { easeOut, configRefs } from '@utils/constants';
import { getDurationValue, CSSRefTransition } from './transition.css.js';
import { sizeTransitionTokens } from './transition.size.selectors.js';
import { jsx } from 'react/jsx-runtime';

/**
 * acts similarily to the base <Transition /> component (which this component
 * actually builds upon), with the addition that `width` and `height`
 * properties can be transitioned, which is not natively supported by browsers.
 *
 * by default components applying this transition will grow from 0 to their
 * original size in the specified axis when entering and the opposite when
 * exiting.
 * If `swappable` is set to true the transition behaves differently. Instead,
 * the parent will be able to transition its size from an exiting
 * <SizeTransition /> to an entering <SizeTransition /> based on the given
 * axis.
 *
 * NB: The `transition` property MUST be declared on the HTMLElement that is
 * wrapped with the <SizeTransition /> with a value including the specified
 * axis.
 *
 * @example
 * ```
 * const contentCls = css`
 *  &-enter-active {
 *    transition: height 200ms linear;
 *  }
 *
 *  &-exit-active {
 *    transition: height 200ms linear;
 *  }
 * `
 *
 * <SizeTransition
 *  in={active}
 *  axis="horisontal"
 *  timeout={200}
 *  classNames={contentCls}
 * >
 *  {(ref) => <div ref={ref} className={contentCls} />}
 * </SizeTransition>
 * ```
 */
function SizeTransition({
  axis,
  swappable,
  skipValidation,
  retainElementPositionDuringAnimation,
  children,
  getMinSizeInPx,
  getMaxSizeInPx,
  strategy,
  ...props
}) {
  const lastEventRef = useRef(undefined);
  const internalRef = useRef(null);
  const nodeRef = props.nodeRef ?? internalRef;
  const elementPositionAnimationFrameRef = useRef(0);
  const onEnter = useCallback(isAppearing => {
    props.onEnter?.(isAppearing);
    lastEventRef.current = "enter";
    const element = nodeRef.current;
    const parent = element?.parentElement;
    if (!element) {
      return;
    }
    const constraints = getComputedConstraints({
      getMaxSizeInPx,
      getMinSizeInPx,
      swappable
    }, element, isAppearing ? "appear" : "enter");

    // in case swappable is provided, then the logic that applies the size
    // should be applied on the parent element
    if (swappable) {
      if (!parent) {
        console.warn("<SizeTransition />: Unable to resolve parent element.");
        return;
      }
      const siblings = getSiblings(element, parent);
      const constrainedHeight = clamp(constraints.minSizeInPx.height, siblings.map(it => it.clientHeight), constraints.maxSizeInPx.height);
      const constrainedWidth = clamp(constraints.minSizeInPx.width, siblings.map(it => it.clientWidth), constraints.maxSizeInPx.width);
      if (["both", "horisontal"].includes(axis) && !parent.style.width) {
        element.style.setProperty(sizeTransitionTokens.initialWidth.propName, `${constrainedWidth}px`);
        parent.style.width = `${constrainedWidth}px`;
      }
      if (["both", "vertical"].includes(axis) && !parent.style.height) {
        element.style.setProperty(sizeTransitionTokens.initialHeight.propName, `${constrainedHeight}px`);
        parent.style.height = `${constrainedHeight}px`;
      }
    } else {
      // ... else apply the size on the element itself as default
      if (["both", "horisontal"].includes(axis)) {
        element.style.setProperty(sizeTransitionTokens.initialWidth.propName, `${constraints.minSizeInPx.width}px`);
        element.style.width = `${constraints.minSizeInPx.width}px`;
      }
      if (["both", "vertical"].includes(axis)) {
        element.style.setProperty(sizeTransitionTokens.initialHeight.propName, `${constraints.minSizeInPx.height}px`);
        element.style.height = `${constraints.minSizeInPx.height}px`;
      }
    }
    if (retainElementPositionDuringAnimation) {
      const initialY = element.getBoundingClientRect().top;
      const scroller = findNearestScroller(element);
      retainScrollPosition({
        animationFrameRef: elementPositionAnimationFrameRef,
        element,
        scroller,
        scrollTarget: typeof retainElementPositionDuringAnimation === "object" ? retainElementPositionDuringAnimation.target ?? "scroller" : "scroller",
        startedAtMs: Date.now(),
        targetY: typeof retainElementPositionDuringAnimation === "object" ? retainElementPositionDuringAnimation.targetY ?? initialY : initialY,
        durationMs: getDurationValue(props.timeout, isAppearing ? "appear" : "enter"),
        initialY: initialY
      });
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [props.onEnter, nodeRef, getMaxSizeInPx, getMinSizeInPx, swappable, retainElementPositionDuringAnimation, axis, props.timeout]);
  const onEntering = useCallback(isAppearing => {
    props.onEntering?.(isAppearing);
    lastEventRef.current = "enter-active";
    const element = nodeRef.current;
    const parent = element?.parentElement;
    if (!element) {
      return;
    }

    // slightly defer running the enter animation, which provides enough time
    // for certain components to properly mount and apply size calculations
    // (e.g a component, which can possibly apply height limitations within a
    // layout effect)
    if (lastEventRef.current !== "enter-active") {
      return;
    }
    const constraints = getComputedConstraints({
      getMaxSizeInPx,
      getMinSizeInPx
    }, element, isAppearing ? "appear-active" : "enter-active");
    const constrainedHeight = clamp(constraints.minSizeInPx.height, strategy === "clientSize" ? element.clientHeight : element.scrollHeight, constraints.maxSizeInPx.height);
    const constrainedWidth = clamp(constraints.minSizeInPx.width, strategy === "clientSize" ? element.clientWidth : element.scrollWidth, constraints.maxSizeInPx.width);

    // in case swappable is provided, then the logic that applies the size
    // should be applied on the parent element
    if (swappable) {
      if (!parent) {
        console.warn("<SizeTransition />: Unable to resolve parent element.");
        return;
      }
      validateElement(parent, {
        axis,
        swappable,
        skipValidation
      });
      if (["both", "horisontal"].includes(axis)) {
        parent.style.width = `${constrainedWidth}px`;
      }
      if (["both", "vertical"].includes(axis)) {
        parent.style.height = `${constrainedHeight}px`;
      }
    } else {
      // ... else apply the size on the element itself as default
      validateElement(element, {
        axis,
        swappable,
        skipValidation
      });
      if (["both", "horisontal"].includes(axis)) {
        element.style.width = `${constrainedWidth}px`;
      }
      if (["both", "vertical"].includes(axis)) {
        element.style.height = `${constrainedHeight}px`;
      }
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [props.onEntering, nodeRef, getMaxSizeInPx, getMinSizeInPx, strategy, swappable, axis, skipValidation]);
  const onEntered = useCallback(isAppearing => {
    props.onEntered?.(isAppearing);
    lastEventRef.current = "enter-done";
    const element = nodeRef.current;
    const parent = element?.parentElement;
    if (!element || !parent) {
      return;
    }
    const target = swappable ? parent : element;
    if (["both", "horisontal"].includes(axis)) {
      element.style.removeProperty(sizeTransitionTokens.initialWidth.propName);
      target.style.width = "";
    }
    if (["both", "vertical"].includes(axis)) {
      element.style.removeProperty(sizeTransitionTokens.initialHeight.propName);
      target.style.height = "";
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [nodeRef, swappable, axis, props.onEntered]);
  const onExit = useCallback(() => {
    window.cancelAnimationFrame(elementPositionAnimationFrameRef.current);
    lastEventRef.current = "exit";
    const element = nodeRef.current;

    // If swappable is set to true then the application of size properties will
    // be handled by the entering element and thus we can safely bail out.
    if (!element || swappable) {
      return;
    }
    if (["both", "horisontal"].includes(axis)) {
      element.style.setProperty(sizeTransitionTokens.initialWidth.propName, `${element.clientWidth}px`);
      element.style.width = `${element.clientWidth}px`;
    }
    if (["both", "vertical"].includes(axis)) {
      element.style.setProperty(sizeTransitionTokens.initialHeight.propName, `${element.clientHeight}px`);
      element.style.height = `${element.clientHeight}px`;
    }
    props.onExit?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeRef, swappable, axis, props.onExit]);
  const onExiting = useCallback(() => {
    props.onExiting?.();
    lastEventRef.current = "exit-active";
    const element = nodeRef.current;

    // If swappable is set to true then the application of size properties will
    // be handled by the entering element and thus we can safely bail out.
    if (!element || swappable) {
      return;
    }

    // slightly defer running the exit animation, which provides enough time
    // for certain components to properly mount and apply size calculations (e.g
    // a component, which can possibly apply height limitations within a layout
    // effect)
    if (lastEventRef.current !== "exit-active") {
      return;
    }
    const constraints = getComputedConstraints({
      getMaxSizeInPx,
      getMinSizeInPx
    }, element, "exit");
    validateElement(element, {
      axis,
      swappable,
      skipValidation
    });
    if (["both", "horisontal"].includes(axis)) {
      element.style.width = `${constraints.minSizeInPx.width}px`;
    }
    if (["both", "vertical"].includes(axis)) {
      element.style.height = `${constraints.minSizeInPx.height}px`;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.onExiting, nodeRef, swappable, getMaxSizeInPx, getMinSizeInPx, axis, skipValidation]);
  const onExited = useCallback(() => {
    lastEventRef.current = "exit-done";
    const element = nodeRef.current;
    if (element) {
      element.style.removeProperty("width");
      element.style.removeProperty("height");
      element.style.removeProperty(sizeTransitionTokens.initialWidth.propName);
      element.style.removeProperty(sizeTransitionTokens.initialHeight.propName);
    }
    props.onExited?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.onExited, nodeRef]);
  return /*#__PURE__*/jsx(CSSRefTransition, {
    ...props,
    nodeRef: nodeRef,
    onEnter: onEnter,
    onEntering: onEntering,
    onEntered: onEntered,
    onExit: onExit,
    onExiting: onExiting,
    onExited: onExited,
    children: children
  });
}

/**
 * In case size constraints has been applied to the element which can be
 * calculated in pixels (or directly provided to this transition) then ensure
 * we limit the transition to these constraints.
 */
function getComputedConstraints(args, element, stage) {
  const computedStyles = window.getComputedStyle(element);
  const minHeight = computedStyles.minHeight === "none" || !computedStyles.minHeight.includes("px") ? 0 : parseFloat(computedStyles.minHeight);
  const maxHeight = computedStyles.maxHeight === "none" || !computedStyles.maxHeight.includes("px") ? Infinity : parseFloat(computedStyles.maxHeight);
  const minWidth = computedStyles.minWidth === "none" || !computedStyles.minWidth.includes("px") ? 0 : parseFloat(computedStyles.minWidth);
  const maxWidth = computedStyles.maxWidth === "none" || !computedStyles.maxWidth.includes("px") ? Infinity : parseFloat(computedStyles.maxWidth);
  const explicitMinSize = args.getMinSizeInPx?.(element, stage);
  const explicitMaxSize = args.getMaxSizeInPx?.(element, stage);
  return {
    minSizeInPx: {
      width: args.swappable ? explicitMinSize?.width ?? 0 : Math.max(minWidth, explicitMinSize?.width ?? 0),
      height: args.swappable ? explicitMinSize?.height ?? 0 : Math.max(minHeight, explicitMinSize?.height ?? 0)
    },
    maxSizeInPx: {
      width: args.swappable ? explicitMaxSize?.width ?? Infinity : Math.min(maxWidth, explicitMaxSize?.width ?? Infinity),
      height: args.swappable ? explicitMaxSize?.height ?? Infinity : Math.min(maxHeight, explicitMaxSize?.height ?? Infinity)
    }
  };
}
function getSiblings(element, parent) {
  const siblings = [];
  for (const child of Array.from(parent.children)) {
    if (child !== element) {
      siblings.push(child);
    }
  }
  return siblings;
}

/**
 * observes the provided element and ensures that it has all the CSS properties
 * required to properly integrate with the <SizeTransition />.
 */
function validateElement(element, args) {
  if (configRefs.env.node.dangerouslyAssertSyncValue() === "production" || args.skipValidation) {
    return;
  }
  const computedStyles = getComputedStyle(element);
  const hasValidHeightTransition = computedStyles.transition !== "all 0s ease 0s" && (computedStyles.transition.includes("all") || computedStyles.transition.includes("height"));
  const hasValidWidthTransition = computedStyles.transition !== "all 0s ease 0s" && (computedStyles.transform.includes("all") || computedStyles.transition.includes("width"));
  const elementLabel = args.swappable ? "parent of the transition element" : "transitioning element";
  if (!hasValidHeightTransition && ["both", "vertical"].includes(args.axis)) {
    console.warn(`<SizeTransition />: The ${elementLabel} must have a CSS 'transition' declaration including the 'height' property`, element);
  }
  if (!hasValidWidthTransition && ["both", "horisontal"].includes(args.axis)) {
    console.warn(`<SizeTransition />: The ${elementLabel} must have a CSS 'transition' declaration including the 'width' property`, element);
  }
}
function retainScrollPosition(args) {
  const currentY = args.element?.getBoundingClientRect().top;
  const t = (Date.now() - args.startedAtMs) / args.durationMs;
  const p = easeOut(Math.min(1, t), 2);
  if (!currentY) {
    return;
  }
  const viewportTarget = (args.targetY - args.initialY) * p;
  const diff = currentY - args.initialY - viewportTarget;
  if (args.scroller && args.scrollTarget === "scroller") {
    args.scroller.scrollTop += diff;
  } else {
    window.scrollTo({
      top: window.scrollY + diff,
      behavior: "auto"
    });
  }
  if (t < 1.2) {
    args.animationFrameRef.current = window.requestAnimationFrame(() => retainScrollPosition(args));
  }
}

export { SizeTransition };
//# sourceMappingURL=transition.size.js.map
